<script>
import CalendarApiService from '@/services/calendarService';
import LineChart from '../charts/LineChart';
import { cloneDeep, isEqual } from 'lodash';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import MoveProgressEventsModal from '@/router/views/dashboards/capacities/components/modals/moveProgressEventsModal';
import EventBus from '@/router/views/dashboards/capacities/eventBus/eventBus';
import UserConfigMixin from '@/mixins/userConfigMixin';
import CapacityService from '@/services/capacity-service';

/**
 * CapacitiesChart Component
 */
export default {
    props: ['model', 'filters'],
    page: {},
    components: {
        LineChart,
        DatePicker,
        MoveProgressEventsModal,
    },
    mixins: [UserConfigMixin],
    data() {
        return {
            isLoading: false,
            isUpdatingValueTypePreference: false,
            openMoveProgressEventsModal: false,
            valueTypes: [],
            valuesForDates: [],
            valuesForDatesCopy: [],
            openEditCapacities: false,
            hideCompleted: false,
            selectedValueType: null,
            selectedValueForDateIndex: null,
            editSectionItems: [],
            blockerEvents: [],
            valueTypesColor: [
                {
                    type: 'Products',
                    color: '#8170ff', //'#580aff'//'#8170ff'
                },
                {
                    type: 'Parts',
                    color: '#6aebb3', //'#147df5'//'#6aebb3'
                },
                {
                    type: 'SheetsQuantity',
                    color: '#ff7091', //'#0aefff'//'#ff7091'
                },
                {
                    type: 'NumericValue',
                    color: '#ffc670', //'#be0aff'//'#ffc670'
                },
                {
                    type: 'SquareFoot',
                    color: '#ff7070', //'#ff8700'//'#ff7070'
                },
                {
                    type: 'SquareMeter',
                    color: '#cbff70', //'#deff0a'//'#cbff70'
                },
                {
                    type: 'TimePerWorkOrder',
                    color: '#70ecff', //'#a1ff0a'//'#70ecff'
                },
                {
                    type: 'DoorsQuantity',
                    color: '#d970ff', //'#0aff99'//'#d970ff'
                },
            ],
            capacities: [],
            options: {
                maintainAspectRatio: false,
                responsive: true,
                onClick: async (evt) => {
                    let chartElem = this.$refs.lineChart.$data._chart;

                    const points = chartElem.getElementsAtEventForMode(
                        evt,
                        'nearest',
                        { intersect: true },
                        true
                    );

                    let data = chartElem.data;

                    if (points.length) {
                        const firstPoint = points[0];
                        let datasetIndex = firstPoint._datasetIndex;
                        let index = firstPoint._index;

                        //open edit section
                        this.openEditCapacities = true;
                        //copy pour le preview dans section edit(pour pouvoir cancel et remettre letat)
                        this.valuesForDatesCopy = cloneDeep(
                            this.valuesForDates
                        );

                        let valueType = data.datasets[datasetIndex].valueType;
                        this.selectedValueType = valueType;
                        this.selectedValueForDateIndex = index;

                        if (this.valuesForDates[index]) {
                            let ids = [];
                            let response;

                            if (this.model.type == 'Progress') {
                                ids = this.valuesForDates[index]
                                    .progressAppointmentsId;
                                response = await CalendarApiService.getProgressAppointmentsWithValues(
                                    ids
                                );

                                if (response != null) {
                                    this.setProgressAppointments(
                                        response.data.data
                                    );
                                }
                            } else if (this.model.type == 'Installation') {
                                ids = this.valuesForDates[index]
                                    .workOrdersIdPublic;
                                response = await CalendarApiService.getWorkOrdersNoWorkOrderProgress(
                                    ids
                                );

                                if (response != null) {
                                    this.setWorkOrders(response.data.data);
                                }
                            } else if (this.model.type == 'Production') {
                                ids = this.valuesForDates[index]
                                    .workOrdersIdPublic;
                                response = await CalendarApiService.getWorkOrdersNoWorkOrderProgress(
                                    ids
                                );

                                if (response != null) {
                                    this.setWorkOrders(response.data.data);
                                }
                            }
                        }
                    } else {
                        this.openEditCapacities = false;
                    }
                },
            },
        };
    },
    async created() {
        EventBus.$on('reload-all-progress-charts', this.loadProgressValues);
        EventBus.$on('reload-all-installer-charts', this.loadInstallerValues);
        EventBus.$on('reload-all-production-charts', this.loadProductionValues);

        this.$bus.$on('locale-updated', this.localeUpdated);

        this.isLoading = true;

        await this.loadCapacitiesAndSelectedValueTypes();

        await this.loadBlockerEvents();

        await this.loadValues();

        this.isLoading = false;

        //Set watch sur filters ici pour etre certain que les capacities et values type sont load avant de get les values
        //probleme de timing si watch est declare sur lobjet vue. de temps en temps load values serait call avant que created ait fini de load les capacites et value types
        this.$watch('filters', (oldValue, newValue) => {
            //verification de la valeur car cest appele meme si filter nest pas diffrent a cause du deepClone des filters du parent
            if (!isEqual(oldValue, newValue)) {
                this.loadValues();
            }
        });
    },
    beforeDestroy() {
        this.$bus.$off('locale-updated', this.localeUpdated);
        EventBus.$off('reload-all-progress-charts', this.loadProgressValues);
        EventBus.$off('reload-all-installer-charts', this.loadInstallerValues);
        EventBus.$off(
            'reload-all-production-charts',
            this.loadProductionValues
        );
    },
    mounted() {},
    methods: {
        setProgressAppointments(progressAppointments) {
            let items = progressAppointments;
            items.forEach((x) => {
                let values = x.values;
                let model = {
                    workOrderName: x.workOrder.name,
                    progressAppointmentId: x.id,
                    valueForType: this.getValueForType(
                        this.selectedValueType,
                        values
                    ),
                    values: values,
                    daySpan: Math.abs(
                        this.$dayjs(x.startDate).diff(x.endDate, 'day')
                    ),
                    startDate: this.$dayjs(x.startDate).toDate(),
                    oldStartDate: this.$dayjs(x.startDate).toDate(),
                    endDate: this.$dayjs(x.endDate).toDate(),
                    oldEndDate: this.$dayjs(x.endDate).toDate(),
                    completed:
                        x.workOrderStepPercentCompleted == 1 ? true : false,
                };

                //ajouter a la liste si le progress appointment nest pas deja la
                if (
                    this.editSectionItems.find((item) => {
                        return item.progressAppointmentId == x.id;
                    }) == undefined
                ) {
                    this.editSectionItems.push(model);
                }
            });
        },
        setWorkOrders(workOrders) {
            workOrders.forEach((x) => {
                let values = x.workOrderValues;
                let model = {
                    workOrder: x,
                    workOrderName: x.name,
                    workOrderIdPublic: x.idPublic,
                    valueForType: this.getValueForType(
                        this.selectedValueType,
                        values
                    ),
                    values: values,
                    daySpan: Math.abs(
                        this.$dayjs(x.installationDate).diff(
                            x.installationEndDate,
                            'day'
                        )
                    ),
                    startDate: this.$dayjs(x.installationDate).toDate(),
                    oldStartDate: this.$dayjs(x.installationDate).toDate(),
                    endDate: this.$dayjs(x.installationEndDate).toDate(),
                    oldEndDate: this.$dayjs(x.installationEndDate).toDate(),
                };

                //ajouter a la liste si le work order nest pas deja la
                if (
                    this.editSectionItems.find((item) => {
                        return item.workOrderIdPublic == x.idPublic;
                    }) == undefined
                ) {
                    this.editSectionItems.push(model);
                }
            });
        },
        localeUpdated() {
            this.$forceUpdate();
        },
        getValueForType: function(type, values) {
            let value = 0;

            let elem = values.filter((x) => {
                return x.measureType == type;
            });

            if (elem.length > 0) {
                elem = elem[0];
                value = elem.value;
            }

            return value;
        },
        getDateLabels: function() {
            let labels = [];

            //Utiliser valuesForDate pour populer les labels
            for (let valuesForDate of this.valuesForDates) {
                let date = valuesForDate.date;

                let dayOfWeek = this.$dayjs(date).day();

                // Skip weekends if hideWeekends is enabled
                if (
                    this.filters.hideWeekends &&
                    (dayOfWeek === 0 || dayOfWeek === 6)
                ) {
                    continue;
                }

                labels.push(this.$dayjs(date).format('L'));
            }

            return labels;
        },
        getValueTypeData: function(valueType) {
            let data = [];

            for (let valuesForDate of this.valuesForDates) {
                let date = valuesForDate.date;
                let dayOfWeek = this.$dayjs(date).day();

                // Skip weekends if hideWeekends is enabled
                if (
                    this.filters.hideWeekends &&
                    (dayOfWeek === 0 || dayOfWeek === 6)
                ) {
                    continue;
                }

                let valueForType = valuesForDate.values.filter((x) => {
                    return x.type == valueType;
                });
                if (valueForType.length > 0) {
                    valueForType = valueForType[0];
                    let totalValue = valueForType.totalValue;
                    let nonCompleted = valueForType.nonCompleted;
                    data.push(this.hideCompleted ? nonCompleted : totalValue);
                }
            }

            return data;
        },
        getValueTypeColor(type) {
            let color = 'blue';

            let colorAssoc = this.valueTypesColor.filter((x) => {
                return x.type == type;
            });
            if (colorAssoc.length > 0) {
                colorAssoc = colorAssoc[0];
                color = colorAssoc.color;
            }

            return color;
        },
        getDataSet() {
            let dataSet = [];

            //for each value type checked
            let checkedValueTypes = this.valueTypes.filter((x) => {
                return x.selected == true;
            });

            //=====VALUES(full line)=====
            //work order steps total values
            for (let value of checkedValueTypes) {
                let valueType = value.valueType;
                let data = this.getValueTypeData(valueType);
                let color = this.getValueTypeColor(valueType);
                let model = {
                    data: data,
                    label: this.$t('general.' + valueType),
                    valueType: valueType,
                    borderColor: color,
                    fill: false,
                };

                dataSet.push(model);
            }

            //=====CAPACITIES(dashed line)=====
            //capacities for selected item
            for (let value of checkedValueTypes) {
                let valueType = value.valueType;
                let dates = this.valuesForDates.map((x) => new Date(x.date));

                let data = CapacityService.getMaximumCapacitiesByPeriod(
                    this.filters.intervalType,
                    this.capacities.filter((x) => {
                        return x.measureType == valueType;
                    }),
                    this.model.item,
                    dates.filter((date) => {
                        // Filter out weekends if hideWeekends is enabled
                        if (this.filters.hideWeekends) {
                            let dayOfWeek = new Date(date).getDay();
                            return dayOfWeek !== 0 && dayOfWeek !== 6;
                        }
                        return true;
                    }),

                    this.filters.dateRange[1],
                    this.blockerEvents
                );
                let color = this.getValueTypeColor(valueType);
                let model = {
                    data: data,
                    label:
                        this.$t('general.' + valueType) +
                        ' ' +
                        this.$t('general.capacity'),
                    valueType: 'Capacity',
                    borderColor: color,
                    fill: false,
                    borderDash: [7, 4],
                    borderWidth: 2,
                    radius: 2,
                };

                dataSet.push(model);
            }

            return dataSet;
        },
        loadCapacitiesAndSelectedValueTypes: async function() {
            let response = await CalendarApiService.getValueTypesSelected(
                this.model.id
            );
            let valueTypesSelected = response.data.data;

            if (this.model.type == 'Progress') {
                let response = await CalendarApiService.getProgressCapacities(
                    this.model.item.map((x) => {
                        return x.stepIdPublic;
                    })
                );
                this.capacities = response.data.data;
            } else if (this.model.type == 'Installation') {
                let response = await CalendarApiService.getInstallersCapacities(
                    this.model.item.map((x) => {
                        return x.idPublic;
                    })
                );
                this.capacities = response.data.data;
            } else if (this.model.type == 'Production') {
                let response = await CalendarApiService.getProductionCapacities();
                this.capacities = response.data;
            }

            //set value types for progress capacities
            for (let capacity of this.capacities) {
                //verif si deja dans la liste
                let isInList = this.valueTypes.findIndex((x) => {
                    return x.valueType == capacity.measureType;
                });
                if (isInList != -1) {
                    continue;
                }

                //verif data du chart pour savoir si une preference a ete sauvegarde dans la BD
                let valueTypeIndex = valueTypesSelected.findIndex((x) => {
                    return x.valueType == capacity.measureType;
                });

                let capacityChartId = this.model.id;
                let valueType = capacity.measureType;
                let selected = true;
                let id = null;

                if (valueTypeIndex != -1) {
                    let valueType = valueTypesSelected[valueTypeIndex];
                    selected = valueType.selected;
                    id = valueType.id;
                }

                let model = {
                    id: id,
                    valueType: valueType,
                    selected: selected,
                    capacityChartId: capacityChartId,
                };

                this.valueTypes.push(model);
            }
        },
        getWorkOrderProgressValuesForDateRangeModel(valueType = null) {
            return {
                startDate: this.filters.dateRange[0],
                endDate: this.filters.dateRange[1],
                intervalType: this.filters.intervalType,
                workOrderTypesIdPublic: !this.filters.workOrderTypes
                    ? null
                    : this.filters.workOrderTypes.map((x) => {
                          return x.idPublic;
                      }),
                progressId: this.model.item.map((x) => {
                    return x.id;
                }),
                measureTypes:
                    valueType == null
                        ? this.valueTypes
                              .filter((x) => {
                                  return x.selected == true;
                              })
                              .map((x) => {
                                  return x.valueType;
                              })
                        : [valueType],
            };
        },
        getWorkOrderValuesForDateRangeModel(valueType = null) {
            return {
                startDate: this.filters.dateRange[0],
                endDate: this.filters.dateRange[1],
                intervalType: this.filters.intervalType,
                workOrderTypesIdPublic: !this.filters.workOrderTypes
                    ? null
                    : this.filters.workOrderTypes.map((x) => {
                          return x.idPublic;
                      }),
                installersIdPublic: this.model.item.map((x) => {
                    return x.idPublic;
                }),
                measureTypes:
                    valueType == null
                        ? this.valueTypes
                              .filter((x) => {
                                  return x.selected == true;
                              })
                              .map((x) => {
                                  return x.valueType;
                              })
                        : [valueType],
            };
        },
        loadValues: async function() {
            if (this.model.type == 'Progress') {
                this.loadProgressValues();
            } else if (this.model.type == 'Installation') {
                this.loadInstallerValues();
            } else if (this.model.type == 'Production') {
                this.loadProductionValues();
            }
        },
        loadBlockerEvents: async function() {
            let model3 = {
                startDate: this.$dayjs(this.filters.dateRange[0]).format(
                    'YYYY-MM-DD'
                ),
                endDate: this.$dayjs(this.filters.dateRange[1]).format(
                    'YYYY-MM-DD'
                ),
                stepIds:
                    this.model.type == 'Progress'
                        ? this.model.item.map((x) => {
                              return x.id;
                          })
                        : [],
                installerIds:
                    this.model.type == 'Installation'
                        ? this.model.item.map((x) => {
                              return x.idPublic;
                          })
                        : [],
            };

            let resp = await CalendarApiService.postGetBlockerEvents3(model3);
            this.blockerEvents = resp.data;
        },
        getProgressValues: async function() {
            let startDate = this.$dayjs(this.filters.dateRange[0]).format(
                'YYYY-MM-DD'
            );
            let endDate = this.$dayjs(this.filters.dateRange[1]).format(
                'YYYY-MM-DD'
            );

            let model2 = {
                startDate: startDate,
                endDate: endDate,
                workOrderTypesIdPublic: !this.filters.workOrderTypes
                    ? null
                    : this.filters.workOrderTypes.map((x) => {
                          return x.idPublic;
                      }),
                progressIds: this.model.item.map((x) => {
                    return x.id;
                }),
            };

            let resp = await CalendarApiService.getProgressCapacitiesForDateRange(
                model2
            );

            return resp.data.data;
        },
        getInstallationValues: async function() {
            let startDate = this.$dayjs(this.filters.dateRange[0]).format(
                'YYYY-MM-DD'
            );
            let endDate = this.$dayjs(this.filters.dateRange[1]).format(
                'YYYY-MM-DD'
            );

            let model2 = {
                startDate: startDate,
                endDate: endDate,
                workOrderTypesIdPublic: !this.filters.workOrderTypes
                    ? null
                    : this.filters.workOrderTypes.map((x) => {
                          return x.idPublic;
                      }),
                installationIds: this.model.item.map((x) => {
                    return x.idPublic;
                }),
            };

            let resp = await CalendarApiService.getInstallationCapacitiesForDateRange(
                model2
            );
            return resp.data.data;
        },
        getProductionValues: async function() {
            let startDate = this.$dayjs(this.filters.dateRange[0]).format(
                'YYYY-MM-DD'
            );
            let endDate = this.$dayjs(this.filters.dateRange[1]).format(
                'YYYY-MM-DD'
            );

            let model2 = {
                startDate: startDate,
                endDate: endDate,
                workOrderTypesIdPublic: !this.filters.workOrderTypes
                    ? null
                    : this.filters.workOrderTypes.map((x) => {
                          return x.idPublic;
                      }),
            };

            let resp = await CalendarApiService.getProductionCapacitiesForDateRange(
                model2
            );

            return resp.data.data;
        },
        loadProgressValues: async function() {
            if (this.model.type != 'Progress') return;

            this.isLoading = true;

            let apiValues = await this.getProgressValues();

            this.valuesForDates = this.formatProgressValues(apiValues);

            this.isLoading = false;
        },
        getFormattedBlockingEvents: function() {
            return this.blockerEvents.map((blocker) => {
                const blockStart = this.$dayjs(blocker.startDate).format(
                    'YYYY-MM-DD'
                );
                const blockEnd = this.$dayjs(blocker.endDate).format(
                    'YYYY-MM-DD'
                );

                return {
                    ...blocker,
                    startDate: new Date(blockStart + 'T00:00:00'),
                    endDate: new Date(blockEnd + 'T00:00:00'),
                };
            });
        },
        formatAppointments: function(appointments, formattedBlockingEvents) {
            const countSpecificDay = function(start, end, specificDayId) {
                let count = 0;

                start = new Date(start);
                end = new Date(end);
                // Loop from the start date to the end date
                while (start < end) {
                    if (start.getDay() === specificDayId) {
                        count++;
                    }
                    // Move to the next day
                    start.setDate(start.getDate() + 1);
                }

                return count;
            };

            return appointments.map((appointment) => {
                const appointmentStart = new Date(
                    this.$dayjs(appointment.progress.startDate).format(
                        'YYYY-MM-DD'
                    ) + 'T00:00:00'
                );
                const appointmentEnd = new Date(
                    this.$dayjs(appointment.progress.endDate).format(
                        'YYYY-MM-DD'
                    ) + 'T00:00:00'
                );

                let days = Math.max(
                    1,
                    Math.ceil(
                        (appointmentEnd - appointmentStart) /
                            (1000 * 60 * 60 * 24)
                    )
                );

                for (let blocker of formattedBlockingEvents) {
                    if (
                        blocker.startDate < appointmentEnd &&
                        blocker.endDate > appointmentStart
                    ) {
                        days -= Math.max(
                            1,
                            Math.floor(
                                (blocker.endDate - blocker.startDate) /
                                    (1000 * 60 * 60 * 24)
                            )
                        );
                    }
                }

                if (!appointment.progress.progress.includeSaturday) {
                    days -= countSpecificDay(
                        appointmentStart,
                        appointmentEnd,
                        6
                    );
                }
                if (!appointment.progress.progress.includeSunday) {
                    days -= countSpecificDay(
                        appointmentStart,
                        appointmentEnd,
                        0
                    );
                }

                return {
                    ...appointment,
                    includeSaturday:
                        appointment.progress.progress.includeSaturday,
                    includeSunday: appointment.progress.progress.includeSunday,
                    days: Math.max(days, 0),
                    startDate: appointmentStart,
                    endDate: appointmentEnd,
                };
            });
        },
        formatWorkOrders: function(workOrders, formattedBlockingEvents) {
            return workOrders.map((workOrder) => {
                const workOrderStart = new Date(
                    this.$dayjs(workOrder.installationDate).format(
                        'YYYY-MM-DD'
                    ) + 'T00:00:00'
                );
                const workOrderEnd = new Date(
                    this.$dayjs(workOrder.installationEndDate).format(
                        'YYYY-MM-DD'
                    ) + 'T00:00:00'
                );

                let days = Math.max(
                    1,
                    Math.ceil(
                        (workOrderEnd - workOrderStart) / (1000 * 60 * 60 * 24)
                    )
                );

                for (let blocker of formattedBlockingEvents) {
                    if (
                        blocker.startDate < workOrderEnd &&
                        blocker.endDate > workOrderStart
                    ) {
                        days -= Math.max(
                            1,
                            Math.floor(
                                (blocker.endDate - blocker.startDate) /
                                    (1000 * 60 * 60 * 24)
                            )
                        );
                    }
                }

                return {
                    ...workOrder,
                    days: Math.max(days, 0),
                    startDate: workOrderStart,
                    endDate: workOrderEnd,
                };
            });
        },
        formatProductionWorkOrders: function(
            workOrders,
            formattedBlockingEvents
        ) {
            return workOrders.map((workOrder) => {
                const workOrderStart = new Date(
                    this.$dayjs(workOrder.productionDate).format('YYYY-MM-DD') +
                        'T00:00:00'
                );
                const workOrderEnd = new Date(
                    this.$dayjs(workOrder.productionDate)
                        .add(1, 'day')
                        .format('YYYY-MM-DD') + 'T00:00:00'
                );
                let days = 1;

                for (let blocker of formattedBlockingEvents) {
                    if (
                        blocker.startDate < workOrderEnd &&
                        blocker.endDate > workOrderStart
                    ) {
                        days -= Math.max(
                            1,
                            Math.floor(
                                (blocker.endDate - blocker.startDate) /
                                    (1000 * 60 * 60 * 24)
                            )
                        );
                    }
                }

                return {
                    ...workOrder,
                    days: Math.max(days, 0),
                    startDate: workOrderStart,
                    endDate: workOrderEnd,
                };
            });
        },
        formatProgressValues: function(appointments) {
            const stringStartDate = this.$dayjs(
                this.filters.dateRange[0]
            ).format('YYYY-MM-DD');
            const stringEndDate = this.$dayjs(this.filters.dateRange[1]).format(
                'YYYY-MM-DD'
            );
            const startDate = new Date(stringStartDate + 'T00:00:00');
            const endDate = new Date(stringEndDate + 'T00:00:00');

            const measureTypes = this.valueTypes
                .filter((x) => {
                    return x.selected == true;
                })
                .map((x) => {
                    return x.valueType;
                });

            let dateRange = [];

            let currentDate = new Date(startDate);

            let formattedBlockingEvents = this.getFormattedBlockingEvents();

            let formattedAppointments = this.formatAppointments(
                appointments,
                formattedBlockingEvents
            );

            let dateValues = null;
            let startOfWeek = currentDate.getDay();
            let startOfMonth = currentDate.getDate();
            while (currentDate <= endDate) {
                if (
                    !dateValues ||
                    this.filters.intervalType == 'days' ||
                    (this.filters.intervalType == 'weeks' &&
                        startOfWeek == currentDate.getDay()) ||
                    (this.filters.intervalType == 'months' &&
                        startOfMonth == currentDate.getDate())
                ) {
                    const year = currentDate.getFullYear();
                    const month = String(currentDate.getMonth() + 1).padStart(
                        2,
                        '0'
                    );
                    const day = String(currentDate.getDate()).padStart(2, '0');
                    const hours = String(currentDate.getHours()).padStart(
                        2,
                        '0'
                    );
                    const minutes = String(currentDate.getMinutes()).padStart(
                        2,
                        '0'
                    );
                    const seconds = String(currentDate.getSeconds()).padStart(
                        2,
                        '0'
                    );

                    dateValues = {
                        date: `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`,
                        values: [],
                        progressAppointmentsId: [],
                    };

                    dateRange.push(dateValues);
                }

                const relevantAppointments = formattedAppointments.filter(
                    (appointment) => {
                        return (
                            appointment.days > 0 &&
                            currentDate >= appointment.startDate &&
                            currentDate < appointment.endDate
                        );
                    }
                );

                let tomorrow = new Date(currentDate);
                tomorrow.setTime(tomorrow.getTime() + 24 * 60 * 60 * 1000);

                for (let measureType of measureTypes) {
                    let data = dateValues.values.find(
                        (x) => x.type == measureType
                    );

                    if (!data) {
                        data = {
                            type: measureType,
                            totalValue: 0,
                            completed: 0,
                            nonCompleted: 0,
                        };
                        dateValues.values.push(data);
                    }

                    if (
                        formattedBlockingEvents.find(
                            (b) =>
                                b.startDate < tomorrow &&
                                b.endDate > currentDate
                        )
                    ) {
                        continue;
                    }

                    for (let appointment of relevantAppointments) {
                        if (
                            !appointment.includeSaturday &&
                            currentDate.getDay() == 6
                        ) {
                            continue;
                        }
                        if (
                            !appointment.includeSunday &&
                            currentDate.getDay() == 0
                        ) {
                            continue;
                        }

                        const value = appointment.values.find(
                            (value) => value.measureType === measureType
                        );

                        if (!value) continue;

                        let amount = value.value / appointment.days;
                        data.totalValue = parseFloat(
                            Number(data.totalValue + amount).toFixed(2)
                        );

                        if (value.workOrderStepStatus == 'Completed') {
                            data.completed = parseFloat(
                                Number(data.completed + amount).toFixed(2)
                            );
                            data.completed += amount;
                        } else {
                            data.nonCompleted = parseFloat(
                                Number(data.nonCompleted + amount).toFixed(2)
                            );
                        }

                        if (
                            !dateValues.progressAppointmentsId.includes(
                                appointment.progress.id
                            )
                        ) {
                            dateValues.progressAppointmentsId.push(
                                appointment.progress.id
                            );
                        }
                    }
                }

                currentDate = tomorrow;
            }

            return dateRange;
        },
        loadInstallerValues: async function() {
            if (this.model.type != 'Installation') return;

            this.isLoading = true;

            let apiValues = await this.getInstallationValues();

            this.valuesForDates = this.formatInstallationValues(apiValues);

            this.isLoading = false;
        },
        loadProductionValues: async function() {
            if (this.model.type != 'Production') return;

            this.isLoading = true;

            let apiValues = await this.getProductionValues();

            this.valuesForDates = this.formatProductionValues(apiValues);

            this.isLoading = false;
        },
        formatInstallationValues: function(workOrders) {
            const stringStartDate = this.$dayjs(
                this.filters.dateRange[0]
            ).format('YYYY-MM-DD');
            const stringEndDate = this.$dayjs(this.filters.dateRange[1]).format(
                'YYYY-MM-DD'
            );
            const startDate = new Date(stringStartDate + 'T00:00:00');
            const endDate = new Date(stringEndDate + 'T00:00:00');

            const measureTypes = this.valueTypes
                .filter((x) => {
                    return x.selected == true;
                })
                .map((x) => {
                    return x.valueType;
                });

            let dateRange = [];

            let currentDate = new Date(startDate);

            let formattedBlockingEvents = this.getFormattedBlockingEvents();

            let formattedWorkOrders = this.formatWorkOrders(
                workOrders,
                formattedBlockingEvents
            );

            let dateValues = null;
            let startOfWeek = currentDate.getDay();
            let startOfMonth = currentDate.getDate();
            while (currentDate <= endDate) {
                if (
                    !dateValues ||
                    this.filters.intervalType == 'days' ||
                    (this.filters.intervalType == 'weeks' &&
                        startOfWeek == currentDate.getDay()) ||
                    (this.filters.intervalType == 'months' &&
                        startOfMonth == currentDate.getDate())
                ) {
                    const year = currentDate.getFullYear();
                    const month = String(currentDate.getMonth() + 1).padStart(
                        2,
                        '0'
                    );
                    const day = String(currentDate.getDate()).padStart(2, '0');

                    dateValues = {
                        date: `${year}-${month}-${day}T00:00:00`,
                        values: [],
                        workOrdersIdPublic: [],
                    };

                    dateRange.push(dateValues);
                }

                const relevantWorkOrders = formattedWorkOrders.filter(
                    (workOrder) =>
                        currentDate >= workOrder.startDate &&
                        currentDate < workOrder.endDate
                );

                let tomorrow = new Date(currentDate);
                tomorrow.setTime(tomorrow.getTime() + 24 * 60 * 60 * 1000);

                for (let measureType of measureTypes) {
                    let data = dateValues.values.find(
                        (x) => x.type == measureType
                    );

                    if (!data) {
                        data = {
                            type: measureType,
                            totalValue: 0,
                            completed: 0,
                            nonCompleted: 0,
                        };
                        dateValues.values.push(data);
                    }

                    if (
                        formattedBlockingEvents.find(
                            (b) =>
                                b.startDate < tomorrow &&
                                b.endDate > currentDate
                        )
                    ) {
                        continue;
                    }

                    for (let workOrder of relevantWorkOrders) {
                        const value = workOrder.workOrderValues.find(
                            (value) => value.measureType === measureType
                        );

                        if (!value) continue;

                        let amount = value.value / workOrder.days;
                        data.totalValue = parseFloat(
                            Number(data.totalValue + amount).toFixed(2)
                        );

                        if (
                            !dateValues.workOrdersIdPublic.includes(
                                workOrder.idPublic
                            )
                        ) {
                            dateValues.workOrdersIdPublic.push(
                                workOrder.idPublic
                            );
                        }
                    }
                }

                currentDate = tomorrow;
            }

            return dateRange;
        },
        formatProductionValues: function(workOrders) {
            const stringStartDate = this.$dayjs(
                this.filters.dateRange[0]
            ).format('YYYY-MM-DD');
            const stringEndDate = this.$dayjs(this.filters.dateRange[1]).format(
                'YYYY-MM-DD'
            );
            const startDate = new Date(stringStartDate + 'T00:00:00');
            const endDate = new Date(stringEndDate + 'T00:00:00');

            const measureTypes = this.valueTypes
                .filter((x) => {
                    return x.selected == true;
                })
                .map((x) => {
                    return x.valueType;
                });

            let dateRange = [];

            let currentDate = new Date(startDate);

            let formattedBlockingEvents = this.getFormattedBlockingEvents();

            let formattedWorkOrders = this.formatProductionWorkOrders(
                workOrders,
                formattedBlockingEvents
            );

            let dateValues = null;
            let startOfWeek = currentDate.getDay();
            let startOfMonth = currentDate.getDate();
            while (currentDate <= endDate) {
                if (
                    !dateValues ||
                    this.filters.intervalType == 'days' ||
                    (this.filters.intervalType == 'weeks' &&
                        startOfWeek == currentDate.getDay()) ||
                    (this.filters.intervalType == 'months' &&
                        startOfMonth == currentDate.getDate())
                ) {
                    const year = currentDate.getFullYear();
                    const month = String(currentDate.getMonth() + 1).padStart(
                        2,
                        '0'
                    );
                    const day = String(currentDate.getDate()).padStart(2, '0');

                    dateValues = {
                        date: `${year}-${month}-${day}T00:00:00`,
                        values: [],
                        workOrdersIdPublic: [],
                    };

                    dateRange.push(dateValues);
                }

                const relevantWorkOrders = formattedWorkOrders.filter(
                    (workOrder) =>
                        currentDate >= workOrder.startDate &&
                        currentDate < workOrder.endDate
                );

                let tomorrow = new Date(currentDate);
                tomorrow.setTime(tomorrow.getTime() + 24 * 60 * 60 * 1000);

                for (let measureType of measureTypes) {
                    let data = dateValues.values.find(
                        (x) => x.type == measureType
                    );

                    if (!data) {
                        data = {
                            type: measureType,
                            totalValue: 0,
                            completed: 0,
                            nonCompleted: 0,
                        };
                        dateValues.values.push(data);
                    }

                    if (
                        formattedBlockingEvents.find(
                            (b) =>
                                b.startDate < tomorrow &&
                                b.endDate > currentDate
                        )
                    ) {
                        continue;
                    }

                    for (let workOrder of relevantWorkOrders) {
                        const value = workOrder.workOrderValues.find(
                            (value) => value.measureType === measureType
                        );

                        if (!value) continue;

                        let amount = value.value / workOrder.days;
                        data.totalValue = parseFloat(
                            Number(data.totalValue + amount).toFixed(2)
                        );

                        if (
                            !dateValues.workOrdersIdPublic.includes(
                                workOrder.idPublic
                            )
                        ) {
                            dateValues.workOrdersIdPublic.push(
                                workOrder.idPublic
                            );
                        }
                    }
                }

                currentDate = tomorrow;
            }

            return dateRange;
        },
        loadValuesForValueType: async function(valueType) {
            let data;

            this.isLoading = true;
            if (this.model.type == 'Progress') {
                let model = this.getWorkOrderProgressValuesForDateRangeModel(
                    valueType
                );
                let resp = await CalendarApiService.getWorkOrderProgressValuesForDateRange(
                    model
                );
                data = resp.data.data;
            } else if (this.model.type == 'Installation') {
                let model = this.getWorkOrderValuesForDateRangeModel();
                let resp = await CalendarApiService.getWorkOrderInstallationValuesForDateRange(
                    model
                );
                data = resp.data.data;
            } else if (this.model.type == 'Production') {
                let model = this.getWorkOrderValuesForDateRangeModel();
                let resp = await CalendarApiService.getWorkOrderProductionValuesForDateRange(
                    model
                );
                data = resp.data.data;
            }
            this.isLoading = false;

            if (data != null) {
                for (let valuesForDate of this.valuesForDates) {
                    let sameDateValues = data.filter((x) => {
                        return x.date == valuesForDate.date;
                    });
                    if (sameDateValues.length > 0) {
                        sameDateValues = sameDateValues[0];
                        let sameDateValuesForType = sameDateValues.values.filter(
                            (x) => {
                                return x.type == valueType;
                            }
                        );

                        let valuesForType = valuesForDate.values.filter((x) => {
                            return x.type == valueType;
                        });
                        if (valuesForType.length > 0) {
                            valuesForType = valuesForType[0];
                            if (sameDateValuesForType.length > 0) {
                                sameDateValuesForType =
                                    sameDateValuesForType[0];
                                valuesForType.totalValue =
                                    sameDateValuesForType.totalValue;
                            }
                        } else {
                            let totalValue = 0;
                            if (sameDateValuesForType.length > 0) {
                                sameDateValuesForType =
                                    sameDateValuesForType[0];
                                totalValue = sameDateValuesForType.totalValue;
                            }
                            let model = {
                                type: valueType,
                                totalValue: totalValue,
                            };

                            valuesForDate.values.push(model);
                        }
                    }
                }
            }
        },
        async toggleValueType(value) {
            if (value.selected == true) {
                //load values
                await this.loadValuesForValueType(value.valueType);
            }

            //save backend
            let model = {
                id: value.id,
                capacityChartId: value.capacityChartId,
                valueType: value.valueType,
                selected: value.selected,
            };

            if (value.id == null) {
                //permet dempecher de recliquer sur la switch avant la creation de la preference en backend
                //loading overlay
                this.isUpdatingValueTypePreference = true;
            }

            let response = await CalendarApiService.setChartValueTypeSelected(
                model
            );
            let id = response.data.data;
            value.id = id;
            this.isUpdatingValueTypePreference = false;
        },
        changeStartDate(item) {
            //faire suivre le end date
            let oldEndDate = this.$dayjs(item.oldEndDate);
            let oldStartDate = this.$dayjs(item.oldStartDate);

            let diff = oldEndDate.diff(oldStartDate, 'day');

            if (this.model.type == 'Production') diff = 1;

            item.endDate = this.$dayjs(item.startDate)
                .add(diff, 'day')
                .toDate();

            this.changeDate(item);
        },
        changeEndDate(item) {
            this.changeDate(item);
        },
        updateIntervalValuesForDateRange(
            appointmentData,
            startDate,
            endDate,
            operation
        ) {
            let dateType =
                this.filters.intervalType == 'days'
                    ? 'day'
                    : this.filters.intervalType == 'weeks'
                    ? 'week'
                    : 'month';
            let i = 0;
            let appointmentTimeSpan = this.$dayjs(endDate).diff(
                startDate,
                'day'
            );
            while (i < appointmentTimeSpan) {
                let currentDateWithoutTime = this.$dayjs(startDate).add(
                    i,
                    'day'
                );
                let valuesForDate = this.valuesForDates.filter((x) => {
                    return (
                        this.$dayjs(currentDateWithoutTime).isBetween(
                            this.$dayjs(x.date),
                            this.$dayjs(x.date).add(1, dateType)
                        ) ||
                        this.$dayjs(currentDateWithoutTime).isSame(
                            this.$dayjs(x.date)
                        )
                    );
                });
                if (valuesForDate.length > 0) {
                    valuesForDate = valuesForDate[0];
                } else {
                    return;
                }

                for (let valueData of valuesForDate.values) {
                    let type = valueData.type;
                    let totalValueForType = valueData.totalValue;

                    let values = appointmentData.values;
                    let valueForType = this.getValueForType(type, values);

                    let valueToAdd = valueForType;
                    if (this.model.type == 'Progress') {
                        valueToAdd = valueForType / appointmentTimeSpan;
                    } else if (this.model.type == 'Installation') {
                        //Si installation, ajouter seulement la partie qui revietn a linstaller(ex.: si work order a 60 produits, et 2 installers, chaque installer aura 30 produits.)
                        //multiplier par le nombre ditem dans la chart
                        valueToAdd = this.getValueToAddForInstallation(
                            appointmentData.workOrder.installers,
                            appointmentTimeSpan,
                            valueForType
                        );
                    }

                    valueData.totalValue =
                        operation == '+'
                            ? totalValueForType + valueToAdd
                            : operation == '-'
                            ? totalValueForType - valueToAdd
                            : 0;
                }

                i++;
            }
        },
        getValueToAddForInstallation(
            installers,
            appointmentTimeSpan,
            valueForType
        ) {
            var valueToAdd = 0;

            var nbOfMatchingInstallerInChart = 0;
            var workOrderInstallersIdPublic = installers.map((x) => {
                return x.idPublic;
            });
            var selectedInstallersIdPublic = this.model.item.map((x) => {
                return x.idPublic;
            });
            nbOfMatchingInstallerInChart = selectedInstallersIdPublic.filter(
                (x) => {
                    return workOrderInstallersIdPublic.includes(x);
                }
            ).length;
            valueToAdd =
                (valueForType / installers.length / appointmentTimeSpan) *
                nbOfMatchingInstallerInChart;

            return valueToAdd;
        },
        changeDate(appointmentData) {
            //enlever les values pour le old interval
            this.updateIntervalValuesForDateRange(
                appointmentData,
                appointmentData.oldStartDate,
                appointmentData.oldEndDate,
                '-'
            );
            //ajouter les values pour le nouvel interval
            this.updateIntervalValuesForDateRange(
                appointmentData,
                appointmentData.startDate,
                appointmentData.endDate,
                '+'
            );

            //set old start date
            appointmentData.oldStartDate = appointmentData.startDate;
            appointmentData.oldEndDate = appointmentData.endDate;
        },
        format(date) {
            return date ? this.$dayjs(date).format('L') : '';
        },
        cancelEdit() {
            this.valuesForDates = cloneDeep(this.valuesForDatesCopy);
            this.openEditCapacities = false;
            this.editSectionItems = [];
        },
        async applyEdit() {
            //envoyer les nouvelles dates
            if (this.model.type == 'Progress') {
                let config = false;
                if (this.model.item.length == 1) {
                    let response = await CalendarApiService.getIsMoveLaterProgress(
                        this.model.item[0].stepIdPublic
                    );
                    config = response.data.data;
                }

                if (config) {
                    //ouvrir modal
                    this.openMoveProgressEventsModal = true;
                } else {
                    //update progress appointments dates
                    await this.editProgressEvents(true);
                }
            } else if (this.model.type == 'Installation') {
                //update reference appointments
                await this.editInstallerEvents();
            } else if (this.model.type == 'Production') {
                //update reference appointments
                await this.editProductionEvents();
            }
        },
        cancelEditEvents() {
            this.openMoveProgressEventsModal = false;
        },
        async editInstallerEvents() {
            let data = [];

            for (let item of this.editSectionItems) {
                let tmp = {
                    type: 'Installation',
                    workOrderIdPublic: item.workOrderIdPublic,
                    startDate: this.$dayjs(item.startDate).format('YYYY-MM-DD'),
                    endDate: this.$dayjs(item.endDate).format('YYYY-MM-DD'),
                    moveBrokenLink: true,
                };

                data.push(tmp);
            }

            this.isLoading = true;
            await CalendarApiService.setReferenceAppointmentsDate(data);
            this.isLoading = false;

            EventBus.$emit('reload-all-installer-charts');

            this.openEditCapacities = false;
            this.editSectionItems = [];

            this.openMoveProgressEventsModal = false;
        },
        async editProductionEvents() {
            let data = [];

            for (let item of this.editSectionItems) {
                let tmp = {
                    type: 'Production',
                    workOrderIdPublic: item.workOrderIdPublic,
                    startDate: this.$dayjs(item.startDate).format('YYYY-MM-DD'),
                    moveBrokenLink: true,
                };

                data.push(tmp);
            }

            this.isLoading = true;
            await CalendarApiService.setReferenceAppointmentsDate(data);
            this.isLoading = false;

            EventBus.$emit('reload-all-production-charts');

            this.openEditCapacities = false;
            this.editSectionItems = [];

            this.openMoveProgressEventsModal = false;
        },
        async editProgressEvents(answer) {
            if (answer != undefined) {
                let model = {
                    moveLaterProgress: answer,
                    progressAppointmentsDates: [],
                };

                for (let item of this.editSectionItems) {
                    let tmp = {
                        progressAppointmentId: item.progressAppointmentId,
                        startDate: this.$dayjs(item.startDate).format(
                            'YYYY-MM-DD'
                        ),
                        endDate: this.$dayjs(item.endDate).format('YYYY-MM-DD'),
                    };

                    model.progressAppointmentsDates.push(tmp);
                }

                this.isLoading = true;
                await CalendarApiService.setProgressAppointmentsDates(model);
                this.isLoading = false;

                //reload
                if (answer == true) {
                    EventBus.$emit('reload-all-progress-charts');
                } else {
                    this.loadValues();
                }
            }

            this.openEditCapacities = false;
            this.editSectionItems = [];

            this.openMoveProgressEventsModal = false;
        },
        getItemsName() {
            let name = '';

            var index = 0;
            this.model.item.forEach((x) => {
                if (index == this.model.item.length - 1) {
                    name += x.name;
                } else {
                    name += x.name + ' | ';
                }

                index++;
            });

            return name;
        },
        removeCapacityChart() {
            CalendarApiService.removeCapacityChart(this.model.id);
            this.$emit('capacity-chart-removed');
        },
    },
    watch: {},
    computed: {
        userCanEditSelectedItems() {
            let disabled = false;

            let type = this.model.type;

            for (var i = 0; i < this.model.item.length; i++) {
                let canEditItem =
                    type == 'Progress'
                        ? this.userCanEditProgress(this.model.item[i])
                        : this.userCanEditInstaller(this.model.item[i]);

                if (canEditItem == false) {
                    disabled = true;
                    break;
                }
            }

            return disabled;
        },
        myStyles() {
            return {
                flex: '1 1 0',
                position: 'relative',
            };
        },
        dataCollectionComputed() {
            let labels = this.getDateLabels();
            let dataSet = this.getDataSet();
            let dataCollection = {
                labels: labels,
                datasets: dataSet,
            };

            return dataCollection;
        },
        orderedEditSectionItems() {
            //ordonner selon le selected value type
            let selectedValueType = this.selectedValueType;

            let sortFn = (a, b) => {
                let aValueForType = this.getValueForType(
                    selectedValueType,
                    a.values
                );
                let bValueForType = this.getValueForType(
                    selectedValueType,
                    b.values
                );

                return aValueForType > bValueForType
                    ? -1
                    : bValueForType > aValueForType
                    ? 1
                    : 0;
            };

            let items = this.editSectionItems.slice().sort(sortFn);
            if (this.hideCompleted) {
                return items.filter((x) => x.completed === false);
            }
            return items;
        },
    },
};
</script>

<template>
    <div
        class="mb-2 p-3 card card-body boxShadow"
        :style="{
            minHeight: openEditCapacities == false ? 370 + 'px' : 850 + 'px',
        }"
        style="
      position: relative;
      min-height: 320px;
      min-width: 0;
      flex: 1 1 0;
      display: flex;
      flex-direction: column;
    "
    >
        <loading
            :active.sync="isLoading"
            style="border-radius: 0.7em"
            :can-cancel="false"
            color="#f1b44c"
            :width="70"
            :height="70"
            loader="dots"
            :is-full-page="false"
        ></loading>
        <div style="display: flex; min-width: 0; align-items: center">
            <p class="mb-0" style="flex: 1 1 0">{{ getItemsName() }}</p>
            <b-form-checkbox
                class="mr-3"
                size="md"
                switch
                v-model="hideCompleted"
            >
                {{ $t('general.hideCompletedProgress') }}
            </b-form-checkbox>
            <b-dropdown
                style="margin-left: auto"
                id="cogDropdown"
                dropleft
                variant="primary"
                :text="$t('general.columns')"
            >
                <template #button-content style="padding: 0">
                    <i style="font-size: 1.6em" class="bx bx-cog"></i>
                </template>
                <b-dropdown-form
                    v-for="(value, index) in valueTypes"
                    :key="index"
                >
                    <loading
                        :active.sync="isUpdatingValueTypePreference"
                        style="border-radius: 0.7em"
                        :can-cancel="false"
                        color="#f1b44c"
                        :width="70"
                        :height="70"
                        loader="dots"
                        :is-full-page="false"
                    ></loading>
                    <b-form-checkbox
                        class="mr-3"
                        size="md"
                        v-model="value.selected"
                        @change="toggleValueType(value)"
                        switch
                        ><span style="white-space: nowrap">{{
                            $t('general.' + value.valueType)
                        }}</span></b-form-checkbox
                    >
                </b-dropdown-form>

                <b-dropdown-divider></b-dropdown-divider>

                <b-dropdown-item
                    v-if="userIsAdmin"
                    @click="removeCapacityChart"
                    variant="danger"
                    >{{ $t('general.delete') }}</b-dropdown-item
                >
            </b-dropdown>
        </div>
        <div
            v-show="openEditCapacities"
            style="display: flex; flex-direction: column"
        >
            <div
                class="mt-2 innerShadow p-2 chromeThinScrollBar"
                style="
          scrollbar-width: thin;
          background: #f8f8f87d;
          border-radius: 0.4em;
          overflow: auto;
          max-height: 370px;
          min-height: 150px;
        "
            >
                <div
                    class="table-responsive mb-0"
                    style="scrollbar-width: thin"
                >
                    <table class="table table-hover" style="zflex: 1 1 0">
                        <thead
                            v-show="false"
                            style="zdisplay: none"
                            class="thead-light"
                        >
                            <tr>
                                <th>{{ $t('general.name') }}</th>
                                <th style="text-align: center"></th>
                                <th
                                    style="text-align: center; width: 200px"
                                ></th>
                                <th
                                    style="width: 100px; text-align: center; width: 200px"
                                ></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, index) in orderedEditSectionItems"
                                :key="index"
                            >
                                <td style="vertical-align: middle">{{
                                    item.workOrderName
                                }}</td>
                                <td
                                    style="text-align: center; vertical-align: middle"
                                >
                                    {{ $t('general.' + selectedValueType) }} :
                                    {{
                                        getValueForType(
                                            selectedValueType,
                                            item.values
                                        )
                                    }}
                                </td>
                                <td style="text-align: center">
                                    <date-picker
                                        :disabled="userCanEditSelectedItems"
                                        @change="changeStartDate(item)"
                                        v-model="item.startDate"
                                        class="mr-2"
                                        style="max-width: 250px"
                                        :first-day-of-week="1"
                                        :formatter="{
                                            stringify: (date) => {
                                                return format(date);
                                            },
                                        }"
                                        :placeholder="$t('general.startDate')"
                                    >
                                    </date-picker>
                                </td>
                                <td v-if="model.type != 'Production'">
                                    <date-picker
                                        :disabled="userCanEditSelectedItems"
                                        @change="changeEndDate(item)"
                                        v-model="item.endDate"
                                        style="max-width: 250px"
                                        :first-day-of-week="1"
                                        :formatter="{
                                            stringify: (date) => {
                                                return date
                                                    ? $dayjs(date).format('L')
                                                    : '';
                                            },
                                        }"
                                        lang="en"
                                        :placeholder="$t('general.endDate')"
                                    >
                                    </date-picker>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="mt-2" style="margin-left: auto">
                <b-button
                    @click="cancelEdit"
                    variant="primary"
                    size="sm"
                    class="mr-2"
                    >{{ $t('general.cancel') }}</b-button
                >
                <b-button @click="applyEdit" variant="primary" size="sm">{{
                    $t('general.apply')
                }}</b-button>
            </div>
        </div>
        <div
            class="chart-container mt-1"
            style="
        flex-grow: 1 1 0;
        min-height: 0;
        min-width: 0;
        background-color: white;
      "
        >
            <LineChart
                ref="lineChart"
                style="position: relative; min-width: 0; width: 100% !important"
                :chart-data="dataCollectionComputed"
                :options="options"
            >
            </LineChart>
        </div>

        <MoveProgressEventsModal
            v-model="openMoveProgressEventsModal"
            @on-answer="editProgressEvents"
            @on-cancel="cancelEditEvents"
        ></MoveProgressEventsModal>
    </div>
</template>

<style>
.chart-container {
    flex-grow: 1;
    min-height: 0;
}

.chart-container > div {
    position: relative;
    height: 100%;
    width: 100% !important;
}
</style>
